import React from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useStatusPalette } from "../../../utils/statusColors";
import {
  STATUS_HUMAN_TAKEOVER,
} from "./conversationConstants";
import {
  buildStatusConfig,
  formatAbsoluteTime,
  buildSessionEmailAddress,
  formatShareId,
} from "./conversationUtils";

function ChatHeader({
  session,
  agentInboxAddress,
  showSessionOperationalMeta = true,
  operatorView = true,
  allowHumanTakeoverControl = false,
  humanTakeoverBusy = false,
  onStartHumanTakeover,
  onEndHumanTakeover,
  actions = null,
}) {
  const _sp = useStatusPalette();
  const STATUS_CONFIG = buildStatusConfig(_sp);
  const st = STATUS_CONFIG[session.status] || STATUS_CONFIG.IN_PROGRESS;
  const isHumanTakeoverActive = session?.status === STATUS_HUMAN_TAKEOVER;
  const sessionEmail = showSessionOperationalMeta
    ? buildSessionEmailAddress(agentInboxAddress, session.emailThreadToken)
    : null;
  const isPublicShareSession = Boolean(
    operatorView &&
      showSessionOperationalMeta &&
      session?.source === "PUBLIC_CHAT" &&
      session?.publicShareId
  );
  const [copied, setCopied] = React.useState(false);
  const [shareIdCopied, setShareIdCopied] = React.useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (sessionEmail) {
      navigator.clipboard.writeText(sessionEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyShareId = (e) => {
    e.stopPropagation();
    if (!isPublicShareSession) return;
    navigator.clipboard.writeText(session.publicShareId);
    setShareIdCopied(true);
    setTimeout(() => setShareIdCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.subtle",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexShrink: 0,
        minHeight: "57px",
        boxSizing: "border-box",
        flexWrap: "wrap",
        py: 1,
      }}
    >
      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>
        {session.title || "Untitled session"}
      </Typography>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          height: "22px",
          px: "8px",
          borderRadius: "6px",
          backgroundColor: st.bg,
          border: `1px solid ${st.border}`,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: st.color,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 500,
            color: st.color,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {st.label}
        </Typography>
      </Box>
      {sessionEmail && (
        <Box
          onClick={handleCopy}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            py: "4px",
            px: "10px",
            borderRadius: "6px",
            backgroundColor: copied ? _sp.greenAlt.bg : "action.hover",
            border: 1,
            borderColor: copied ? _sp.greenAlt.border : "divider",
            cursor: "pointer",
            transition: "all 150ms ease",
            "&:hover": { backgroundColor: copied ? _sp.greenAlt.bg : "action.selected" },
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              color: copied ? _sp.greenAlt.color : "text.secondary",
              lineHeight: 1.4,
              wordBreak: "break-all",
            }}
          >
            {sessionEmail}
          </Typography>
          {copied && (
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                color: _sp.greenAlt.color,
                lineHeight: 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Copied!
            </Typography>
          )}
        </Box>
      )}
      {isPublicShareSession && (
        <Tooltip
          title={`Share ID: ${session.publicShareId}`}
          placement="top"
          arrow
        >
          <Box
            onClick={handleCopyShareId}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              py: "4px",
              px: "10px",
              borderRadius: "6px",
              backgroundColor: shareIdCopied ? _sp.greenAlt.bg : "action.hover",
              border: 1,
              borderColor: shareIdCopied ? _sp.greenAlt.border : "divider",
              cursor: "pointer",
              transition: "all 150ms ease",
              "&:hover": {
                backgroundColor: shareIdCopied ? _sp.greenAlt.bg : "action.selected",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: shareIdCopied ? _sp.greenAlt.color : "text.primary",
                lineHeight: 1.4,
                wordBreak: "break-all",
              }}
            >
              {shareIdCopied
                ? "Share ID copied"
                : `Shared link \u00b7 ${formatShareId(session.publicShareId)}`}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {allowHumanTakeoverControl && (
        <Button
          variant={isHumanTakeoverActive ? "contained" : "outlined"}
          size="small"
          disabled={humanTakeoverBusy}
          onClick={() =>
            isHumanTakeoverActive
              ? onEndHumanTakeover?.()
              : onStartHumanTakeover?.()
          }
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            minHeight: "28px",
            borderRadius: "7px",
            px: 1.25,
            ...(isHumanTakeoverActive
              ? {
                  backgroundColor: _sp.teal.color,
                  color: "#fff",
                  "&:hover": { backgroundColor: _sp.teal.color, opacity: 0.85 },
                }
              : {
                  borderColor: _sp.teal.border,
                  color: _sp.teal.color,
                  "&:hover": {
                    borderColor: _sp.teal.color,
                    backgroundColor: _sp.teal.bg,
                  },
                }),
          }}
        >
          {humanTakeoverBusy
            ? "Updating..."
            : isHumanTakeoverActive
            ? "End takeover"
            : "Start takeover"}
        </Button>
      )}
      <Box sx={{ flex: 1 }} />
      {actions}
      <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
        {formatAbsoluteTime(session.createdAt)}
      </Typography>
    </Box>
  );
}

export default ChatHeader;
